import { FC, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';
import PreferencesFormData from './../interfaces/PreferencesFormData';
import useUpdatePreferences from '../hooks/useUpdatePreferences';
import { useUser } from '@zoopla/user';
import { trackAuthSegmentEvent } from '../utils/segmentTrack';
import { PreferencesProps } from './Preferences.types';
import { trackOptin, trackSubmit } from './Preferences.tracking';
import { EmailPreferencesToggle } from '../common/EmailPreferencesToggle';
import {
    buttonContainerStyle,
    reassuranceTextStyle,
    styledGeneralErrorStyle
} from '../AuthModal.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes';

const PreferencesForm: FC<PreferencesProps> = ({ close, actionAfterAuth }) => {
    const {
        control,
        handleSubmit,
        formState: { errors }
    } = useForm<PreferencesFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });
    const currentUserSession = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { updatePreferences, generalError } = useUpdatePreferences();

    const onFormSubmit: SubmitHandler<PreferencesFormData> = async (
        formData
    ) => {
        setIsSubmitting(true);

        const payload = await updatePreferences(formData);
        const { consent__insights__24 } = formData;

        const parsedConsent = consent__insights__24 === 'yes' ? true : false;

        if (!payload?.data?.updateUserConsents) {
            return;
        }

        if (consent__insights__24 !== undefined) {
            trackOptin(parsedConsent, actionAfterAuth?.eventLabel);

            if (parsedConsent) {
                trackAuthSegmentEvent(
                    'optin',
                    actionAfterAuth?.segment,
                    currentUserSession
                );
            }
        }

        trackSubmit(actionAfterAuth?.eventLabel);

        setIsSubmitting(false);

        // passing the currentUserSession to be able to see if they
        // are a new registration for confirmation screen
        close(currentUserSession);
    };

    const onSubmit = handleSubmit(onFormSubmit);

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            data-testid="auth-modal-preferences-form"
        >
            <div role="alert">
                {generalError && (
                    <Box pb="S500">
                        <span
                            className={`${styledGeneralErrorStyle} ${fontGroups.fork}`}
                            data-testid="general-error"
                        >
                            Sorry, something went wrong. Please try again.
                        </span>
                    </Box>
                )}
            </div>

            <Box pb="M100" bpM100={{ pb: 'S500' }}>
                <Controller
                    control={control}
                    name="consent__insights__24"
                    rules={{ required: true }}
                    render={({ field: { onChange, name, ref, onBlur } }) => (
                        <EmailPreferencesToggle
                            legend="Email preferences"
                            label="I want to hear about the latest property news, advice and new home developments from Zoopla and its selected partners only."
                            errorText={
                                errors.consent__insights__24 &&
                                'Please set your email preferences.'
                            }
                            name={name}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                        />
                    )}
                />
            </Box>

            <Box pb="M100">
                <p
                    className={`${reassuranceTextStyle} ${fontGroups.fork}`}
                    data-testid="unsubscribe-text"
                >
                    You can unsubscribe or update your preferences at any time.
                </p>
            </Box>

            <Box pb="S500" display="flex" justifyContent="center">
                <div className={buttonContainerStyle}>
                    <Button
                        category="primary"
                        loading={isSubmitting}
                        loadingText="Saving"
                        data-testid="update-preferences-button"
                        fw
                    >
                        Save
                    </Button>
                </div>
            </Box>
        </form>
    );
};

export default PreferencesForm;
